import { Dispatch, SetStateAction, useCallback } from 'react'
import { useSnackbar } from 'notistack'
import { t } from '@lingui/macro'

import { decodeBase64Utf8 } from '../lib/helpers'
import { useDmpDocumentNotification } from './useDmpDocumentNotification'
import { useDmpDocumentsNotification } from './useDmpDocumentsNotification'
import { useEmailAttachmentNotification } from './useEmailAttachmentNotification'
import { DmpStatus } from './types'
import { extractDmpStatus, extractMssAccountEmail, isSuccessResponse } from '../lib/xmlParser'

type UseMessageEventParams = {
  patientId: string
  dmpStatus: DmpStatus
  setDmpStatus: Dispatch<SetStateAction<DmpStatus>>
  testDmpReqId: string
  openDmpReqId: string
  getStatusReqId?: string
  /** Called with the connected MSSanté mailbox address parsed from the getStatus answer. */
  onMssAccountEmail?: (email: string) => void
}

export function useMessageEvent({ patientId, setDmpStatus, testDmpReqId, openDmpReqId, getStatusReqId, onMssAccountEmail }: UseMessageEventParams) {
  const { enqueueSnackbar } = useSnackbar()

  const { onDmpDocumentNotification } = useDmpDocumentNotification(patientId)
  const { onDmpDocumentsNotification } = useDmpDocumentsNotification(patientId)
  const { onEmailAttachmentNotification } = useEmailAttachmentNotification(patientId)

  // Handle testDmpExistence response
  const handleTestDmpExistenceResponse = useCallback(
    (xmlData: string) => {
      const status = extractDmpStatus(xmlData)
      console.log('[DmpFlow] DMP status:', status)

      switch (status) {
        case 'open':
        case 'closed':
          setDmpStatus('exists')
          break
        case 'no-dmp':
          setDmpStatus('no-dmp')
          enqueueSnackbar(t`Patient has no DMP`, { variant: 'warning' })
          break
        default:
          setDmpStatus('error')
          enqueueSnackbar(t`Error checking DMP`, { variant: 'error' })
          break
      }
    },
    [setDmpStatus, enqueueSnackbar]
  )

  // Handle openDmp response
  const handleOpenDmpResponse = useCallback(
    (xmlData: string) => {
      if (!isSuccessResponse(xmlData)) {
        console.error('[DmpFlow] Failed to open DMP')
        enqueueSnackbar(t`Failed to open DMP`, { variant: 'error' })
      }
    },
    [enqueueSnackbar]
  )

  const onMessageEvent = useCallback(
    (messageData: { type: string; data?: string }) => {
      if (messageData.type !== 'remoteNotification' || !messageData.data) return

      const decodedData = decodeBase64Utf8(messageData.data)
      console.log('[EfficienceWeb] Decoded response:', decodedData)

      // Handle testDmpExistence response
      if (decodedData.includes('<answer') && decodedData.includes(testDmpReqId)) {
        handleTestDmpExistenceResponse(decodedData)
        return
      }

      // Handle openDmp response
      if (decodedData.includes('<answer') && decodedData.includes(openDmpReqId)) {
        handleOpenDmpResponse(decodedData)
        return
      }

      // Handle getStatus response (connected MSSanté mailbox address)
      if (getStatusReqId && decodedData.includes('<answer') && decodedData.includes(getStatusReqId)) {
        const email = extractMssAccountEmail(decodedData)
        console.log('[MssFlow] getStatus answer, MSS account email:', email)
        if (email && onMssAccountEmail) onMssAccountEmail(email)
        return
      }

      // Handle DMPDocuments notification (multiple documents)
      if (decodedData.includes('<notification data="DMPDocuments"')) {
        console.log('[EfficienceWeb] Multiple documents notification received')
        onDmpDocumentsNotification(decodedData)
        return
      }

      // Handle DMPDocument notification (single document)
      if (decodedData.includes('<notification data="DMPDocument"')) {
        console.log('[EfficienceWeb] Document notification received')
        onDmpDocumentNotification(decodedData)
        return
      }

      // Handle MSS email notification (attachments)
      if (decodedData.includes('<notification data="email"')) {
        console.log('[EfficienceWeb] Email notification received')
        onEmailAttachmentNotification(decodedData)
        return
      }

      // Other notifications
      console.log('[EfficienceWeb] Other notification:', decodedData)
    },
    [
      testDmpReqId,
      openDmpReqId,
      getStatusReqId,
      onMssAccountEmail,
      handleTestDmpExistenceResponse,
      handleOpenDmpResponse,
      onDmpDocumentNotification,
      onDmpDocumentsNotification,
      onEmailAttachmentNotification,
    ]
  )

  return { onMessageEvent }
}
